import React, { useLayoutEffect, useRef, useState } from 'react'

import {
  HeatGridCell,
  HeatGridColumn,
  HeatGridColumnGroup,
  HeatGridData,
  HeatGridLabels,
  HeatGridProps,
  HeatGridRow,
  HeatGridRowGroup,
  PlotStatView,
  PlotStatViews,
} from '@digital-twins/slide-schemas/generated/plot-types'

import { LightboxImage } from '../../components/primitives/LightboxImage/LightboxImage'
import { ChartLabel } from '../_shared/ChartLabel'
import { FastTooltip } from '../_shared/FastTooltip'
import { SectionHeader } from '../_shared/SectionHeader'
import { StatTabs } from '../_shared/StatTabs'
import type { PlotViewport } from '../contract'
import { formatHeatAverage, formatHeatValue } from './format'
import { MATRIX_RATING_CHROME, matrixCellHeight } from './HeatGridMatrix'
import { HEAT_EMPTY_TEXT, HEAT_LEGEND_STEPS, heatFill, heatText } from './style'

/**
 * The shared heat table (plots plan Q): rows = items, columns = measures,
 * options, bins or changes. Every figure, heat, mark and mean is Python's;
 * this draws them.
 *
 * - Heat paints through one ramp with the registry floor (`style.ts`); a cell
 *   with no heat (a change column, an empty cell) paints neutral.
 * - `mark: strongest` draws the ring; the plot never decides a winner.
 * - A change column prints its signed figure and never takes heat.
 * - Column groups (a measure over its versions) print the measure once above
 *   the version columns; row groups print their name (and thumbnail) once.
 * - Averages are drawn only where Python stamped them.
 * - `renderMode: static` is a printed page: labels wrap whole, nothing
 *   scrolls, no hover. Interactive labels clamp in CSS and keep the full name
 *   on hover (a label that failed its repair ships whole: owner decision 15).
 *
 * - Base marks (SC): `mark: suppressed` prints "–" with no heat (the figure
 *   was withheld under the floor, named on hover); `mark: thin` prints its
 *   figure with "~" (directional, named on hover). A change figure is tinted
 *   by the `direction` Python stamped (better green, worse red, none
 *   neutral). The legend names the scale's anchor words (`minLabel` /
 *   `maxLabel`) beside its ends in the Average view when every measured
 *   column shares them; a row's `description` is its label's tooltip.
 *
 * - Average | Top N (decision 20, SS-3): a grid Python stamped with `views`
 *   draws the stat tabs (interactive only) and opens on `defaultView`. In
 *   Top N a cell prints its Top N share (its Bottom N share on hover),
 *   paints `viewHeat` and rings by `viewMarks`; the key names the counted
 *   points once when Python stamped one key (`topBoxesKey`), else each
 *   column names its own (`boxLabels`) beside its label. A ringed cell's key
 *   (`plot_strongest_key`) sits beside the colour key. The grid's rows and columns never change
 *   with the tab. A static grid (a deck exhibit, cut split or segment view)
 *   draws no tabs and prints its stamped `defaultView`, the view its writer
 *   read (`heat_grid_plot.drawn_view` projects the same figures).
 *
 * - Theme grids (W7-T4b): a row group with a `tone` is a sentiment section,
 *   its header drawn in that tone (the shared section header); a row typed
 *   `role: other` is the analysis's rollup (last, never crowned, stamped so);
 *   a column's `description` is its label's tooltip.
 *
 * Cards, the survey tab, the deck segment views and the cut split chart draw
 * this; the deck matrix-rating slide draws `HeatGridMatrix`, the same data at
 * the slide's geometry.
 */

type HeatLabels = Partial<HeatGridLabels>

export interface HeatGridPlotData extends Omit<HeatGridData, 'labels'> {
  labels: HeatLabels
}

export interface HeatGridPlotProps {
  data: HeatGridPlotData
  props?: HeatGridProps
  /** Drawn above the grid (a question heading, a split title). */
  title?: React.ReactNode
  /** Drawn under a row's label (the cut split's base and read pill). */
  rowBadge?: (row: HeatGridRow) => React.ReactNode
  /** Drawn under the grid. */
  footer?: React.ReactNode
  /** Prefix of every test id (hosts keep their own). */
  testIdPrefix?: string
  /** Width of the row-label track; the static split passes its measured box. */
  rowLabelWidth?: number
  /** Whether `data.notes` and the truncation note are drawn here (a host may draw them itself). */
  showNotes?: boolean
  /** Whether the colour key is drawn (hosts with their own legend pass false). */
  showLegend?: boolean
  /** The stat view drawn, when the host holds it (else the plot holds it, from `defaultView`). */
  activeView?: PlotStatView
  onViewChange?: (view: PlotStatView) => void
  /** Legacy stored payloads print today's figure string; stamped payloads never set it. */
  formatCellValue?: (value: number) => string
  /** Legacy stored payloads: what today's grid printed in a cell with no value. */
  emptyCellText?: string
  /** False when the host holds the view with its own control (one switch per choice). Default true. */
  showTabs?: boolean
  /**
   * The height the host measured for this plot when its panel shrinks to its
   * content (a finding exhibit). Without it the grid fills its own box: a
   * fixed panel's height, or, on a page that grows with its content, its
   * cells' ceilings.
   */
  viewport?: PlotViewport
}

/**
 * What a withheld (suppressed) cell prints: a hyphen, never a zero (the glyph W6-S prints for
 * an empty cell; no en or em dash in user-facing copy). Stored results keep the legacy table's.
 */
export const HEAT_SUPPRESSED_TEXT = '-'
/** The directional mark beside a thin cell's figure. */
export const HEAT_THIN_MARK = '~'
const CHANGE_BETTER = 'var(--slide-diverging-positive, #1d9e75)'
const CHANGE_WORSE = 'var(--slide-diverging-negative, #d85a30)'

const ROW_LABEL_MAX = 176
const AVG_COL_WIDTH = 72
const INTERACTIVE_LABEL_LINES = 3
const HEADER_LABEL_LINES = 2

function fill(template: string | undefined, values: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  )
}

/**
 * Interactive labels clamp through the shared rule (`ChartLabel`, plots plan
 * LB): an ellipsis, and the (i) with the whole value on hover and focus. A long
 * unbroken word may still break anywhere, as before.
 */
const INTERACTIVE_WRAP: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'anywhere' }

/**
 * The stamped figures StatTabs reads to offer this grid's views: the first
 * cell Python gave `stats` (every drawn cell has them when a view is offered).
 * The Top N tab label is already filled by Python ("Top 2 / Top 3").
 */
function tabStatsFor(data: HeatGridPlotData): PlotStatViews {
  for (const row of data.rows) {
    for (const cell of row.cells) {
      if (cell.stats) {
        return cell.stats
      }
    }
  }
  return { mean: 0 }
}

/** A cell's figure, heat and ring in the drawn view (Python stamped each). */
export function cellInView(cell: HeatGridCell | undefined, view: PlotStatView) {
  if (!cell || view === 'mean') {
    return { value: cell?.value ?? null, heat: cell?.heat ?? null, isStrongest: cell?.mark === 'strongest', secondary: null }
  }
  const stats = cell.stats
  const value = view === 'topBoxes' ? stats?.topBoxes ?? null : stats?.nps ?? null
  return {
    value,
    heat: view === 'topBoxes' ? cell.viewHeat?.topBoxes ?? null : cell.viewHeat?.nps ?? null,
    isStrongest: (cell.viewMarks ?? []).includes(view),
    secondary: view === 'topBoxes' ? stats?.bottomBoxes ?? null : null,
  }
}

/** The view a grid draws for a requested one: that view when the grid offers it, else Average. */
export function heatGridDrawnView(data: Pick<HeatGridPlotData, 'views'>, requested: PlotStatView | null | undefined): PlotStatView {
  return requested && (data.views ?? ['mean']).includes(requested) ? requested : 'mean'
}

/**
 * How a measure cell's figure prints in a stat view: Top N as a share, NPS as a
 * signed score ("+25", "−5", "0", its key's ends too), at the grid's stamped
 * decimals. The Average view prints each column's own format instead.
 */
export function heatGridStatFormat(view: PlotStatView, data: Pick<HeatGridPlotData, 'statDecimals'>) {
  return {
    valueFormat: view === 'topBoxes' ? 'percent' : view === 'nps' ? 'nps' : 'mean',
    decimals: data.statDecimals ?? 0,
  } as const
}

const WHOLE: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'break-word' }
/** A printed column heading: whole, at the type its box was measured in (`slide-boxes.json`: 10px/1.2). */
const STATIC_HEADER: React.CSSProperties = { ...WHOLE, display: 'block', fontSize: 10, lineHeight: 1.2, minWidth: 0 }

/** Columns in draw order, with the group each one opens (for the group header row). */
function columnLayout(columns: ReadonlyArray<HeatGridColumn>, groups: ReadonlyArray<HeatGridColumnGroup>) {
  const byId = new Map(groups.map((group) => [group.id, group]))
  const spans: Array<{ group: HeatGridColumnGroup | null; span: number; first: number }> = []
  columns.forEach((column, index) => {
    const group = column.groupId ? byId.get(column.groupId) ?? null : null
    const last = spans[spans.length - 1]
    if (last && group && last.group?.id === group.id) {
      last.span += 1
    } else {
      spans.push({ group, span: 1, first: index })
    }
  })
  return spans
}

export function HeatGridPlot(props: HeatGridPlotProps) {
  const {
    data,
    title,
    rowBadge,
    footer,
    testIdPrefix = 'heat-grid',
    rowLabelWidth,
    showNotes = true,
    showLegend = true,
    onViewChange,
    formatCellValue,
    emptyCellText,
    showTabs = true,
    viewport,
  } = props

  const [ownView, setOwnView] = useState<PlotStatView | null>(null)

  const renderProps: HeatGridProps = props.props ?? {}
  const isStatic = renderProps.renderMode === 'static'
  const locale = renderProps.locale
  const labels = data.labels
  const views: ReadonlyArray<PlotStatView> = data.views ?? ['mean']
  const requestedView = props.activeView ?? ownView ?? renderProps.activeView ?? data.defaultView
  // One resolver for every grid, static or not: the host's or the tab's view,
  // else the stamped default (NA-09 s05 printed means beside prose citing the
  // stamped Top 2 share).
  const view: PlotStatView = heatGridDrawnView(data, requestedView)
  const statFormat = heatGridStatFormat(view, data)
  const columns = data.columns
  // A printed grid cannot expose varying usable bases through hover.
  const showCellBases = isStatic && new Set(data.rows.flatMap((row) =>
    row.cells.map((cell) => cell.n).filter((n) => typeof n === 'number'),
  )).size > 1
  const groups = data.columnGroups ?? []
  const hasGroups = groups.length > 0
  // The row and column means are Average figures: drawn in the Average view only.
  const showAverageColumn =
    view === 'mean' &&
    (renderProps.showAverageColumn ?? true) &&
    data.rows.some((row) => typeof row.average === 'number')
  const showColumnMeans =
    view === 'mean' &&
    (renderProps.showColumnMeans ?? false) &&
    columns.some((column) => typeof column.average === 'number')
  const showRowN = renderProps.showRowN ?? false
  const rowGroupById = new Map((data.rowGroups ?? []).map((group) => [group.id, group]))
  // One cell-height owner for every heat grid (`matrixCellHeight`, the matrix
  // slide's): the design's ceiling for the row count, floored against the
  // pixels the rows may take. Those are the box's height less everything that
  // is not a row (title, key, headers, gaps, footer, notes), measured in the
  // page, so a grid fills its panel up to the ceilings and a short grid lets
  // its panel shrink to it.
  const rootRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const [rowsBudget, setRowsBudget] = useState(Number.POSITIVE_INFINITY)
  const height = matrixCellHeight(data.rows.length, rowsBudget)
  const maxHeight = viewport?.maxHeight
  useLayoutEffect(() => {
    const root = rootRef.current
    const scroll = scrollRef.current
    const grid = gridRef.current
    if (!root || !scroll || !grid) {
      return
    }
    const measure = () => {
      const box = maxHeight ?? root.clientHeight
      // A box with no layout yet (a hidden tab) keeps the ceilings until it has one.
      if (box <= 0) {
        return
      }
      // Everything drawn, the scroll region counted at its grid's height (an
      // interactive region stretches to the box), less the row tracks.
      const last = root.lastElementChild as HTMLElement | null
      const drawn = (last ? last.offsetTop + last.offsetHeight : 0) - scroll.offsetHeight + grid.offsetHeight
      const tracks = Array.from(grid.querySelectorAll<HTMLElement>('[data-heat-row-track]'))
      const tracksPx = tracks.reduce((sum, track) => sum + track.offsetHeight, 0)
      // The rows' budget in `matrixCellHeight`'s terms: cells plus its own gaps.
      const next = box - (drawn - tracksPx) + Math.max(0, tracks.length - 1) * MATRIX_RATING_CHROME.rowGap
      setRowsBudget((previous) => (previous === next ? previous : next))
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(root)
    void document.fonts?.ready.then(measure)
    return () => observer?.disconnect()
  }, [maxHeight, data])
  // A printed grid never breaks a word: each column is at least as wide as its
  // longest word, and a fixed label column gives way to the columns only when
  // their words need the room (MT-02 S05 printed "encourag e", L11). When every
  // word fits, the tracks are the measured ones (`slide-boxes.json`).
  const labelTrack = rowLabelWidth
    ? isStatic ? `minmax(min-content, ${rowLabelWidth}px)` : `${rowLabelWidth}px`
    : `fit-content(${ROW_LABEL_MAX}px)`
  const columnTrack = isStatic ? 'minmax(min-content, 1fr)' : 'minmax(0, 1fr)'
  const colTemplate = `${labelTrack} repeat(${Math.max(columns.length, 1)}, ${columnTrack})${
    showAverageColumn ? ` ${AVG_COL_WIDTH}px` : ''
  }`
  const trackCount = 1 + columns.length + (showAverageColumn ? 1 : 0)

  if (data.status === 'unsupported') {
    return (
      <div data-testid={`${testIdPrefix}-unsupported`} style={{ fontSize: 13, color: 'var(--slide-text-secondary)' }}>
        {labels.unsupported_rating_scale ?? null}
      </div>
    )
  }

  // The key's ends: the scale's own bounds when the grid has one shared scale
  // (0% and 100% on a split, 1 and 5 on a rating), else the chrome words.
  const scaleFormat = columns.find((column) => column.kind !== 'change')
  const legendRange: [number, number] | null =
    view === 'topBoxes' ? [0, 100] : view === 'nps' ? [-100, 100] : data.scale ? [data.scale.min, data.scale.max] : null
  const legendFormat = view === 'mean' ? scaleFormat?.valueFormat : statFormat.valueFormat
  const legendLow = legendRange && legendFormat
    ? formatHeatValue(legendRange[0], { valueFormat: legendFormat, decimals: 0 }, locale)
    : labels.scale_low
  const legendHigh = legendRange && legendFormat
    ? formatHeatValue(legendRange[1], { valueFormat: legendFormat, decimals: 0 }, locale)
    : labels.scale_high
  // The scale's anchor words beside the key's ends (Average view, one shared pair only).
  const measured = columns.filter((column) => column.kind !== 'change')
  const anchorsShared =
    measured.length > 0 &&
    measured.every((column) => column.minLabel === measured[0].minLabel && column.maxLabel === measured[0].maxLabel)
  const anchorLow = view === 'mean' && anchorsShared ? measured[0].minLabel ?? null : null
  const anchorHigh = view === 'mean' && anchorsShared ? measured[0].maxLabel ?? null : null
  // The key runs light to dark, worse to better. Python stamps whether the Average view's key
  // swaps its ends (`keyReversed`: every heat column lower-is-better); Top N and NPS heat are the
  // favourable share, so their keys never swap.
  const isKeyReversed = view === 'mean' && data.keyReversed === true
  // Top N: the points the view counts, named once in the key when Python stamped one key for
  // every column (`topBoxesKey`), else inline beside each column's label. Never under the label:
  // the grid must not move between tabs.
  const legendPoints = view === 'topBoxes' ? data.topBoxesKey ?? null : null
  // The ring's key: drawn when the view rings a cell (Python's `mark_strongest`).
  const strongestKey =
    labels.plot_strongest_key &&
    data.rows.some((row) =>
      columns.some(
        (column) => cellInView(row.cells.find((cell) => cell.columnId === column.id), column.kind === 'measure' ? view : 'mean').isStrongest,
      ),
    )
      ? labels.plot_strongest_key
      : null
  const onTab = (next: PlotStatView) => {
    setOwnView(next)
    onViewChange?.(next)
  }
  const statTabs =
    !isStatic && showTabs && views.length > 1 ? (
      <StatTabs
        stats={tabStatsFor(data)}
        activeView={view}
        labels={{
          average: data.statLabels?.average,
          topBoxes: data.statLabels?.topBoxes,
          nps: data.statLabels?.nps,
        }}
        onViewChange={onTab}
      />
    ) : null
  // The colour key explains heat: a grid with no heat in any cell or view has none.
  const hasHeat = data.rows.some((row) =>
    row.cells.some(
      (cell) =>
        typeof cell.heat === 'number' ||
        Object.values(cell.viewHeat ?? {}).some((value) => typeof value === 'number'),
    ),
  )
  // Each end of the key: the scale's number outermost, its anchor word beside the swatches.
  const legendEnd = (end: 'low' | 'high', side: 'left' | 'right') => {
    const number = end === 'low' ? legendLow : legendHigh
    const anchor = end === 'low' ? anchorLow : anchorHigh
    const numberEl = number ? <span data-testid={`${testIdPrefix}-legend-${end}`}>{number}</span> : null
    const anchorEl = anchor ? <span data-testid={`${testIdPrefix}-legend-anchor-${end}`}>{anchor}</span> : null
    return side === 'left' ? (
      <>
        {numberEl}
        {anchorEl}
      </>
    ) : (
      <>
        {anchorEl}
        {numberEl}
      </>
    )
  }
  const legend = showLegend && hasHeat ? (
    <div
      data-testid={`${testIdPrefix}-legend`}
      data-reversed={isKeyReversed ? 'true' : undefined}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        fontSize: 10,
        color: 'var(--slide-text-muted)',
      }}
    >
      {strongestKey ? (
        <span
          data-testid={`${testIdPrefix}-legend-strongest`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 4, whiteSpace: 'nowrap' }}
        >
          <i
            style={{
              display: 'block',
              width: 14,
              height: 10,
              borderRadius: 2,
              boxShadow: 'inset 0 0 0 2px var(--slide-text-primary, #111827)',
            }}
          />
          {strongestKey}
        </span>
      ) : null}
      {legendPoints ? (
        <span data-testid={`${testIdPrefix}-legend-points`} style={{ marginRight: 4 }}>
          {legendPoints}
        </span>
      ) : null}
      {/* The ends and the swatches wrap as one unit, so a narrow card never splits the key. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
        {legendEnd(isKeyReversed ? 'high' : 'low', 'left')}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {HEAT_LEGEND_STEPS.map((step) => (
            <i
              key={step}
              style={{ display: 'block', width: 18, height: 10, borderRadius: 2, background: heatFill(step) }}
            />
          ))}
        </div>
        {legendEnd(isKeyReversed ? 'low' : 'high', 'right')}
      </span>
    </div>
  ) : null

  const headerCell: React.CSSProperties = {
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: 4,
    fontSize: 11,
    lineHeight: 1.2,
    textAlign: 'center',
    color: 'var(--slide-text-secondary)',
    minWidth: 0,
  }

  const groupHeaderRow = hasGroups ? (
    <>
      <div />
      {columnLayout(columns, groups).map((entry) => (
        <div
          key={`group-${entry.first}`}
          data-testid={entry.group ? `${testIdPrefix}-column-group` : undefined}
          data-group-id={entry.group?.id}
          style={{
            ...headerCell,
            gridColumn: `span ${entry.span}`,
            fontWeight: 600,
            color: 'var(--slide-text-primary)',
            borderBottom: entry.group ? '1px solid var(--slide-border-soft, #e2e2e2)' : undefined,
            ...(isStatic ? {} : { gap: 3 }),
          }}
        >
          {isStatic ? (
            <span style={{ width: '100%', ...WHOLE }}>{entry.group?.label ?? ''}</span>
          ) : (
            <ChartLabel
              as="span"
              text={entry.group?.label ?? ''}
              hintIndex={data.rows.length + columns.length + entry.first}
              hintSize={10}
              maxLines={HEADER_LABEL_LINES}
              style={{ ...INTERACTIVE_WRAP, minWidth: 0 }}
            />
          )}
        </div>
      ))}
      {showAverageColumn ? <div /> : null}
    </>
  ) : null

  const renderRowLabel = (row: HeatGridRow) => {
    const protectedName = row.kind === 'asset' || row.kind === 'segment'
    const base = typeof row.base === 'number' ? fill(labels.plot_base_n, { n: row.base }) : null
    return (
      <div
        data-testid={`${testIdPrefix}-row-label`}
        data-row-id={row.id}
        data-role={row.role ?? undefined}
        // Spans its row's track (content centred as before), so the grid can measure the row.
        data-heat-row-track=""
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          fontSize: 13,
          lineHeight: 1.2,
          color: 'var(--slide-text-primary)',
          minWidth: 0,
        }}
      >
        {row.imageUrl && renderProps.showImages !== 'never' ? (
          <LightboxImage
            src={row.imageUrl}
            alt={row.label}
            data-testid={`${testIdPrefix}-row-thumbnail`}
            style={{ display: 'block', height: 28, width: 'auto', borderRadius: 4, marginBottom: 3 }}
          />
        ) : null}
        {isStatic || protectedName ? (
          <div data-testid={`${testIdPrefix}-row-label-text`} style={WHOLE}>
            {row.label}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
            <ChartLabel
              data-testid={`${testIdPrefix}-row-label-text`}
              text={row.label}
              hintIndex={data.rows.indexOf(row)}
              hintSize={10}
              maxLines={INTERACTIVE_LABEL_LINES}
              description={row.description}
              style={{ ...INTERACTIVE_WRAP, minWidth: 0 }}
            />
          </div>
        )}
        {rowBadge ? rowBadge(row) : null}
        {!rowBadge && showRowN && base ? (
          <div data-testid={`${testIdPrefix}-row-n`} style={{ fontSize: 10, color: 'var(--slide-text-muted)', lineHeight: 1.3 }}>
            {base}
          </div>
        ) : null}
      </div>
    )
  }

  const renderCell = (row: HeatGridRow, column: HeatGridColumn) => {
    const cell = row.cells.find((entry) => entry.columnId === column.id)
    const shown = cellInView(cell, column.kind === 'measure' ? view : 'mean')
    const heat = column.kind === 'change' ? null : shown.heat
    const format = view !== 'mean' && column.kind === 'measure' ? statFormat : column
    const text =
      formatCellValue && typeof shown.value === 'number' && Number.isFinite(shown.value) && view === 'mean'
        ? formatCellValue(shown.value)
        : formatHeatValue(shown.value, format, locale)
    const secondaryText = formatHeatValue(shown.secondary, statFormat, locale)
    const isEmpty = text === null
    const isStrongest = shown.isStrongest
    // Base marks (SC): a withheld cell prints the glyph and names why; a thin one reads directional.
    const baseMark = cell?.mark === 'suppressed' || cell?.mark === 'thin' ? cell.mark : null
    const baseNote =
      baseMark === 'suppressed' ? labels.plot_too_few_to_score : baseMark === 'thin' ? labels.plot_low_base_directional : undefined
    const direction = column.kind === 'change' ? cell?.direction ?? 'none' : 'none'
    const changeColor = direction === 'better' ? CHANGE_BETTER : direction === 'worse' ? CHANGE_WORSE : 'var(--slide-text-primary)'

    const emphasized = data.emphasizedColumnId != null && column.id === data.emphasizedColumnId
    // A cell's own base (a version's respondents), named on hover.
    const cellBase = typeof cell?.n === 'number' ? fill(labels.plot_base_n, { n: cell.n }) : null
    const hoverLabel =
      !isStatic && (!isEmpty || baseMark === 'suppressed')
        ? [
            `${column.label}: ${isEmpty ? baseNote ?? HEAT_SUPPRESSED_TEXT : text}`,
            secondaryText && column.boxLabels ? `${column.boxLabels.bottom}: ${secondaryText}` : null,
            !isEmpty && baseNote ? baseNote : null,
            cellBase,
          ]
            .filter(Boolean)
            .join(', ')
        : undefined
    const cellNode = (
      <div
        key={`${row.id}-${column.id}`}
        data-testid={`${testIdPrefix}-cell`}
        data-row-id={row.id}
        data-column-id={column.id}
        data-empty={isEmpty ? 'true' : 'false'}
        data-strongest={isStrongest ? 'true' : 'false'}
        data-change={column.kind === 'change' ? 'true' : 'false'}
        data-direction={column.kind === 'change' ? direction : undefined}
        data-mark={baseMark ?? undefined}
        data-emphasized={emphasized ? 'true' : undefined}
        data-view={view}
        style={{
          height,
          borderRadius: 4,
          background: column.kind === 'change' ? 'var(--slide-bg, #ffffff)' : heatFill(heat),
          border: column.kind === 'change' ? '1px solid var(--slide-border-soft, #e5e7eb)' : undefined,
          boxShadow: isStrongest ? 'inset 0 0 0 2px var(--slide-text-primary, #111827)' : undefined,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: height >= 68 ? 16 : 12,
          fontWeight: 600,
          lineHeight: 1.15,
          color: isEmpty ? HEAT_EMPTY_TEXT : column.kind === 'change' ? changeColor : heatText(heat),
        }}
      >
        <span>
          {text ?? (baseMark === 'suppressed' ? HEAT_SUPPRESSED_TEXT : emptyCellText ?? '')}
          {baseMark === 'thin' && !isEmpty ? (
            <span data-testid={`${testIdPrefix}-cell-thin`} style={{ marginLeft: 2, fontSize: 10, fontWeight: 500, opacity: 0.8 }}>
              {HEAT_THIN_MARK}
            </span>
          ) : null}
        </span>
        {showCellBases && cellBase ? (
          <span data-testid={`${testIdPrefix}-cell-base`} style={{ marginTop: 3, fontSize: 10, fontWeight: 400, lineHeight: 1.2 }}>
            {cellBase}
          </span>
        ) : null}
      </div>
    )
    if (!hoverLabel) {
      return cellNode
    }
    return <FastTooltip key={`${row.id}-${column.id}`} label={hoverLabel}>{cellNode}</FastTooltip>
  }

  const truncationNote =
    data.truncation && data.truncation.shown < data.truncation.total
      ? fill(labels.n_of_total_shown, { shown: data.truncation.shown, total: data.truncation.total })
      : null

  let previousGroupId: string | null | undefined
  return (
    <div
      data-testid={testIdPrefix}
      data-render-mode={isStatic ? 'static' : 'interactive'}
      ref={rootRef}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', minHeight: 0 }}
    >
      {title ? (
        <div
          data-testid={`${testIdPrefix}-title`}
          style={{ marginBottom: 10, fontSize: 16, fontWeight: 600, lineHeight: 1.2, color: 'var(--slide-text-primary)' }}
        >
          {title}
        </div>
      ) : null}
      {(legend || statTabs) ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: isStatic ? 'flex-start' : 'space-between', gap: 12, marginBottom: 8 }}>
          {statTabs ?? <span />}
          {legend}
        </div>
      ) : null}
      <div
        ref={scrollRef}
        data-testid={`${testIdPrefix}-scroll-region`}
        style={
          isStatic
            ? { flexShrink: 0, minWidth: 0, overflow: 'visible' }
            : { flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', scrollbarWidth: 'thin' }
        }
      >
        <div
          ref={gridRef}
          data-testid={`${testIdPrefix}-grid`}
          role="table"
          style={{
            display: 'grid',
            gridTemplateColumns: colTemplate,
            minWidth: hasGroups && !isStatic ? ROW_LABEL_MAX + columns.length * 64 : undefined,
            columnGap: 4,
            rowGap: isStatic ? 4 : 6,
            alignItems: 'center',
          }}
        >
          {groupHeaderRow}
          <div style={{ ...headerCell, justifyContent: 'flex-start' }} />
          {columns.map((column) => {
            const emphasized = data.emphasizedColumnId != null && column.id === data.emphasizedColumnId
            return (
              <div
                key={column.id}
                data-testid={`${testIdPrefix}-column`}
                data-column-id={column.id}
                data-emphasized={emphasized ? 'true' : undefined}
                style={{
                  ...headerCell,
                  fontWeight: emphasized ? 600 : undefined,
                  color: emphasized ? 'var(--slide-text-primary)' : headerCell.color,
                  boxShadow: emphasized ? 'inset 0 -2px 0 var(--slide-chart-1, #0ebfa5)' : undefined,
                  ...(isStatic ? {} : { gap: 3 }),
                }}
              >
                <span style={{ width: '100%', minWidth: 0 }}>
                  {isStatic ? (
                    <span data-testid={`${testIdPrefix}-column-text`} style={{ width: '100%', ...STATIC_HEADER }}>
                      {column.label}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        gap: 3,
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      <ChartLabel
                        as="span"
                        data-testid={`${testIdPrefix}-column-text`}
                        text={column.label}
                        hintIndex={data.rows.length + columns.indexOf(column)}
                        hintSize={10}
                        maxLines={HEADER_LABEL_LINES}
                        description={column.description}
                        style={{ ...INTERACTIVE_WRAP, minWidth: 0 }}
                      />
                      {view === 'topBoxes' && !legendPoints && column.boxLabels ? (
                        <span
                          data-testid={`${testIdPrefix}-column-points`}
                          style={{ flexShrink: 0, fontSize: 10, color: 'var(--slide-text-muted)' }}
                        >
                          {column.boxLabels.top}
                        </span>
                      ) : null}
                    </span>
                  )}
                </span>
              </div>
            )
          })}
          {showAverageColumn ? (
            <div data-testid={`${testIdPrefix}-avg-column`} style={{ ...headerCell, fontWeight: 600 }}>
              {labels.avg_column ?? ''}
            </div>
          ) : null}

          {data.rows.map((row) => {
            const group: HeatGridRowGroup | undefined = row.groupId ? rowGroupById.get(row.groupId) : undefined
            const opensGroup = group !== undefined && row.groupId !== previousGroupId
            previousGroupId = row.groupId
            return (
              <React.Fragment key={row.id}>
                {opensGroup && group && group.tone ? (
                  <div style={{ gridColumn: `1 / span ${trackCount}`, paddingTop: 4 }}>
                    <SectionHeader
                      group={{ id: group.id, label: group.label, tone: group.tone }}
                      testIdPrefix={`${testIdPrefix}-row-section`}
                    />
                  </div>
                ) : opensGroup && group ? (
                  <div
                    data-testid={`${testIdPrefix}-row-group`}
                    data-group-id={group.id}
                    style={{
                      gridColumn: `1 / span ${trackCount}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--slide-text-primary)',
                    }}
                  >
                    {group.imageUrl ? (
                      <img src={group.imageUrl} alt="" style={{ height: 22, width: 'auto', borderRadius: 4 }} />
                    ) : null}
                    <span style={WHOLE}>{group.label}</span>
                  </div>
                ) : null}
                {renderRowLabel(row)}
                {columns.map((column) => renderCell(row, column))}
                {showAverageColumn ? (
                  <div
                    data-testid={`${testIdPrefix}-row-average`}
                    style={{
                      height,
                      borderRadius: 4,
                      border: '1px solid var(--slide-border-soft, #e5e7eb)',
                      background: 'var(--slide-bg, #ffffff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: height >= 68 ? 15 : 12,
                      fontWeight: 600,
                      color: 'var(--slide-text-primary)',
                    }}
                  >
                    {formatHeatAverage(row.average, columns, locale) ?? ''}
                  </div>
                ) : null}
              </React.Fragment>
            )
          })}

          {showColumnMeans ? (
            <>
              <div
                data-testid={`${testIdPrefix}-column-mean-label`}
                style={{ fontSize: 11, color: 'var(--slide-text-muted)', lineHeight: 1.2 }}
              >
                {labels.plot_average ?? ''}
              </div>
              {columns.map((column) => (
                <div
                  key={`mean-${column.id}`}
                  data-testid={`${testIdPrefix}-column-mean`}
                  style={{ fontSize: 11, color: 'var(--slide-text-muted)', textAlign: 'center' }}
                >
                  {formatHeatValue(column.average ?? null, column, locale) ?? ''}
                </div>
              ))}
              {showAverageColumn ? <div /> : null}
            </>
          ) : null}
        </div>
      </div>
      {footer ? <div style={{ flexShrink: 0, marginTop: 8 }}>{footer}</div> : null}
      {showNotes && (truncationNote || (data.notes && data.notes.length > 0)) ? (
        <div
          data-testid={`${testIdPrefix}-notes`}
          style={{ display: 'flex', flexWrap: 'wrap', columnGap: 12, marginTop: 8, fontSize: 11, color: 'var(--slide-text-muted)' }}
        >
          {truncationNote ? <span data-testid={`${testIdPrefix}-truncation`}>{truncationNote}</span> : null}
          {(data.notes ?? []).map((note, index) => (
            <span key={index} data-testid={`${testIdPrefix}-note-${index}`}>
              {note}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
